
import { Component, Prop, Vue } from 'vue-property-decorator'

import { availabilityThreshold } from '@/helpers/AvailabilityHelpers'
import { conflictingSessions } from '@/helpers/ConflictHelpers'
import { findCurrency } from '@/helpers/Currency'
import { annotateSession } from '@/helpers/DynamicMessages'
import { categoriesThatMayConflict, environment } from '@/helpers/Environment'
import { SessionsGroup, getSessionGroups, toTixTab } from '@/helpers/SessionGroups'
import { formatSessionList, hideSoldOutSessions } from '@/helpers/SessionHelpers'

import AnnotationBadges from '@/components/elements/AnnotationBadges.vue'
import AvailabilityStatus from '@/components/elements/AvailabilityStatus.vue'
import Price from '@/components/elements/Price.vue'
import SelectableButtons, { type SelectableButton } from '@/components/elements/SelectableButtons.vue'
import TixLegend, { LegendProps } from '@/components/elements/TixLegend.vue'
import TixTabs from '@/components/elements/TixTabs.vue'
import DynamicMessageGroup from '@/components/events/DynamicMessageGroup.vue'
import FormValidationGuard from '@/components/forms/FormValidationGuard.vue'
import RadioGroup from '@/components/forms/RadioGroup.vue'

import type { EventDetails, SessionPickerTabsConfig } from '@/api/types/processedEntities'
import type { Badge } from '@/calendar/types'
import type { LanguageStrings } from '@/language/types'
import type { AnnotatedSession, Session } from '@/types/Sessions'
import type { TixTab } from '@/types/TixTab'

interface Item {
  session: AnnotatedSession
  label: string
  badges: Badge[]
}

@Component({
  name: 'SelectSession',
  components: {
    TixLegend,
    DynamicMessageGroup,
    FormValidationGuard,
    TixTabs,
    Price,
    RadioGroup,
    AvailabilityStatus,
    AnnotationBadges,
    SelectableButtons,
  },
})
export default class extends Vue {
  @Prop({ required: true })
  value: AnnotatedSession | null

  @Prop({ required: true })
  sessions: Session[]

  @Prop({ required: true })
  event: EventDetails

  @Prop()
  quantities: TicketTypeQuantities

  @Prop({ default: false })
  isAllDayEvent: boolean

  @Prop({ default: false })
  showPrices: boolean

  @Prop()
  mockCapacityThreshold?: number | false

  @Prop()
  tabsConfig: SessionPickerTabsConfig

  t: LanguageStrings['selectSession']

  selectedTabId: string | null = null

  mounted() {
    if (this.displayedSessions.length === 1) {
      this.$emit('input', this.displayedSessions[0])
    }
  }

  created() {
    const firstAvailableTab = this.sessionGroups?.find((t) => t.unavailableReason == undefined)
    if (firstAvailableTab) {
      this.selectedTabId = firstAvailableTab.id
    }
  }

  get displayedSessions(): AnnotatedSession[] {
    return this.nonConflictingSessions
      .filter((session) => this.showSoldOutTimes || !session.sold_out)
      .map((session) => annotateSession(session, this.event, this.quantities))
  }

  // This is necessary for `legendProps` as we shouldn't show annotations or price increase in the legend if all the
  // sessions with them are sold out.
  get availableDisplayedSessions() {
    return this.displayedSessions.filter((session) => !session.sold_out)
  }

  get legendProps(): LegendProps {
    return {
      annotations: this.availableDisplayedSessions.map((session) => session.annotations).flat(),
      // Sold-out selectable session buttons are marked clearly with "sold out" on the button itself.
      // The legend should not display anything for sold out sessions.
      areAnySoldOut: false,
      arePricesDisplayed: this.showPrices,
      hasPriceIncrease: this.availableDisplayedSessions.some(
        (session) => session.priceIncrease && session.priceIncrease > 0,
      ),
    }
  }

  get formattedConflictingSessions(): string {
    return formatSessionList(this.conflictingSessions.filter((session) => !session.sold_out))
  }

  private get nonConflictingSessions(): Session[] {
    if (this.isAllDayEvent) {
      return this.sessions
    } else {
      const conflicting = new Set(this.conflictingSessions.map((session) => session.id))
      return this.sessions.filter((session) => !conflicting.has(session.id))
    }
  }

  get showSoldOutTimes(): boolean {
    return !hideSoldOutSessions(this.event, environment)
  }

  get conflictingSessions(): Session[] {
    // TODO Should this should return [] on all day events? Test handling of all day events that conflict other sessions in the cart.
    if (categoriesThatMayConflict.has(this.event.category)) {
      return []
    } else {
      return conflictingSessions(this.event.id, this.event.meta, this.sessions, this.$store.getters['Cart/cartItems'])
    }
  }

  get title() {
    return this.$t('selectAvailableSession.title')
  }

  get threshold() {
    const threshold = this.mockCapacityThreshold ?? environment.web.show_remaining_session_capacity_threshold ?? false
    const items = this.displayedSessions.map((session) => session.availableCapacity)
    return availabilityThreshold(items, threshold)
  }

  get sessionGroups(): SessionsGroup[] | null {
    // TODO Only enable tabs if config is valid;
    //   - first & second names are not empty, even after trimming whitespace.
    //   - thresholds are valid times; 0000 to 2400, but not e.g. 1260, which does not exist
    //   - if second threshold is set;
    //     - third name is not empty
    //     - first threshold is before second threshold
    if (this.tabsConfig?.enabled) return getSessionGroups(this.displayedSessions, this.tabsConfig)
    else return null
  }

  get tabs(): TixTab[] | null {
    if (this.sessionGroups) return this.sessionGroups.map(toTixTab)
    else return null
  }

  get availableSessions(): AnnotatedSession[] {
    return this.displayedSessions.filter((session) => !session.sold_out)
  }

  get displayedSessionsButtons(): SelectableButton<Item>[] {
    const selectedTab = this.sessionGroups?.find((t) => t.id === this.selectedTabId)
    const sessions = selectedTab ? selectedTab.sessions : this.displayedSessions
    return sessions.map((session) => this.process(session))
  }

  get internal(): Item | undefined {
    return this.value ? this.formatItem(this.value) : undefined
  }

  set internal(newValue: Item | undefined) {
    // Extract the session from the Item and emit it to the parent
    this.$emit('input', newValue?.session)
  }

  private formatItem(session: AnnotatedSession): Item {
    return {
      session,
      label: session.startTime.format('h:mm A'),
      badges: this.badges(session),
    }
  }

  private process(session: AnnotatedSession): SelectableButton<Item> {
    return {
      id: session.id,
      ariaLabel: `${session.startTime.format('h:mm A')} session`,
      classes: ['session', session.sold_out ? 'sold-out' : 'available'],
      disabled: session.sold_out,
      item: this.formatItem(session),
    }
  }

  private badges(session: AnnotatedSession): Badge[] {
    const result: Badge[] = []

    if (session.priceIncrease && session.priceIncrease > 0) {
      result.push({
        symbol: findCurrency(environment.portal.default_currency_code)?.symbol,
        style: 'increase',
      })
    }

    for (const annotation of session.annotations) {
      result.push({
        style: annotation.style,
      })
    }

    return result
  }
}
